import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MutationFunction, QueryFunction, useRequest } from '@frontend/api';

import type { VendorMaterialPricingPlan } from '../types';
import { getUseGetVendorsMaterialPricingPlanQueryKey } from '../useGetVendorMaterialPricingPlan/utils';
import type {
  UseGetUseVendorMaterialPricingPlanCalculationQueryKey,
  VendorMaterialPricingPlanCalculationRequest,
  VendorMaterialPricingPlanCalculationResponse,
} from './types';
import { getUseVendorMaterialPricingPlanCalculationQueryKey } from './utils';

export const useVendorMaterialPricingPlanCalculationRequestQuery: QueryFunction<
  VendorMaterialPricingPlanCalculationResponse,
  [body: VendorMaterialPricingPlanCalculationRequest],
  UseGetUseVendorMaterialPricingPlanCalculationQueryKey
> = (body, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  return useQuery({
    ...queryOptions,
    queryKey: getUseVendorMaterialPricingPlanCalculationQueryKey(body),
    queryFn: ({ signal }) =>
      request('/vendors/vendor_material_pricing_plan_calculation', {
        init: {
          signal,
          method: 'POST',
          body: { ...body },
        },
      }),
    enabled: body.vendorMaterialPricingPlanId ? true : false,
  });
};

export const useVendorMaterialPricingPlanCalculationRequestMutation: MutationFunction<
  VendorMaterialPricingPlanCalculationResponse,
  VendorMaterialPricingPlanCalculationRequest
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);
  return useMutation(
    ({ ...body }: VendorMaterialPricingPlanCalculationRequest) =>
      request('/vendors/vendor_material_pricing_plan_calculation', {
        query,
        init: {
          method: 'POST',
          body,
        },
      }),
    {
      ...queryOptions,
      mutationKey: ['vendors', 'vendorMaterialPricingPlanCalculationRequest'],
      onSuccess: (...args) => {
        queryOptions?.onSuccess?.(...args);
        const data = args[1];
        if (!data.isPreview) {
          queryClient.setQueryData(
            getUseGetVendorsMaterialPricingPlanQueryKey({
              vendorMaterialPricingPlanId: data.vendorMaterialPricingPlanId,
            }),
            (prev: VendorMaterialPricingPlan) => ({
              ...prev,
              costingStrategy: data.costingStrategy,
              selectionCriteria: data.selectionCriteria,
              customSelections: data.customSelections,
            }),
          );
        }
      },
    },
  );
};
